import { useEffect, useMemo, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import { isEmpty, sortBy } from "lodash"
import { Area, Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Line, Brush } from "recharts";
import { useTheme } from "@mui/material/styles";
import { Typography } from "@mui/material";
import AppCard from "@plannel/components/ui/AppCard";
import { orange, red } from "@mui/material/colors";
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import PieChartIcon from '@mui/icons-material/PieChart';
import rpFillRateService from "@plannel/services/rp/rp-fill-rate-service";
import Box from "@mui/material/Box";

import { RPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import RatioBarCell from "@plannel/components/aggrid/RatioBarCell";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import ChartCommonConfigModal from "@plannel/components/modal/ChartCommonConfigModal";
import { SettingsButton } from "@plannel/components/ActionIconButton";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import numberUtil from "@plannel/utils/number-util";
import debug from "@plannel/utils/debug-util";
import p13nService from "@plannel/services/system/p13n-service";

import "@plannel/scss/components/FillRateDashboard.scss";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import LocationTreeFilter from "@plannel/components/filter/LocationTreeFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import RpVersionFilter from "@plannel/components/filter/RpVersionFilter";
import { AgStyleTooltipContent, AgTooltipBox, AgTooltipRow } from "@plannel/components/rechart/AgStyleTooltip";
import { FS } from "@plannel/utils/font-util";

const {rpMeasure, versionStatusCode} = CodeUtil;
const { DEMAND_QTY, BACKORDER_QTY } = rpMeasure;
const ON_TIME_SHIPMENT = "onTimeQty";
const SHORT_QTY = "shortQty";
const DELAYED_QTY = "delayedQty";
const TARGET_RATE = "targetRate";
const SHORT_RATE = "shortRate";
const DELAY_RATE = "delayedRate";
const ON_TIME_RATE = "onTimeRate";
const { SIMULATION, PLANNED, RELEASED } = versionStatusCode;
// 시리즈 색 — 사용자 팔레트(Personal Settings)를 따르도록 ColorUtil.colorSet 으로 잡는다.
//   지연·부족은 "예외"라서 팔레트가 아니라 의미색(경고/위험)을 그대로 쓴다.
const ON_TIME_COLOR = ColorUtil.colorSet(0);
const TOTAL_COLOR = ColorUtil.colorSet(1);
const DELAY_COLOR = orange[400];
const TARGET_COLOR = ColorUtil.colorSet(2);
const SHORT_COLOR = red[400];

const FillRateDashboard = ({ t, viewName, title }) => {
  const theme = useTheme();
  const reduxDispatch = useDispatch(); 
  const { isPermitted } = useMenuAccess();
  const reduxViewState = reduxUtil.getViewState(viewName);
  const exceptionChartRef = useRef();

  const [isLoading, setIsLoading] = useState(false);

  const [brushStartEndIndex, setBrushStartEndIndex] = useState({startIndex: 0, endIndex: 3});
  const [brushYPosition, setBrushYPosition] = useState(0);

  const [rpVersionFilter, setRpVersionFilter] = useState(reduxViewState?.rpVersion ? reduxViewState.rpVersion : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const [rpTotalExceptionBarChartData, setRpTotalExceptionBarChartData] = useState([]);
  const [rpTotalExceptionPieChartData, setRpTotalExceptionPieChartData] = useState([]);
  
  const [fillRateCategoryData, setFillRateCategoryData] = useState([]);

  const [chartConfigOpen, setChartConfigOpen] = useState(false);
  const [chartModalResultData, setChartModalResultData] = useState({});
  const [chartId, setChartId] = useState("");

  const defaultGridMemo = useMemo(() => {
    const defaultSettings = DefaultGridSetting({ title, viewName });
    defaultSettings.defaultColDef.filter = true;
    return defaultSettings;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Bar Chart Option (input seq sort)
  // typeOptions.fill 은 차트 설정 모달의 색 칸용 — 차트는 barChartDefaultOptions 의 같은 색으로 그린다.
  const barChartDefaultSeries = [
    { id: TARGET_RATE, field: TARGET_RATE, text: TARGET_RATE, type: "bar", typeOptions: { fill: TARGET_COLOR } },
    { id: ON_TIME_RATE, field: ON_TIME_RATE, text: ON_TIME_RATE, type: "bar", typeOptions: { fill: ON_TIME_COLOR } },
    { id: DELAY_RATE, field: DELAY_RATE, text: DELAY_RATE, type: "bar", typeOptions: { fill: DELAY_COLOR } },
    { id: SHORT_RATE, field: SHORT_RATE, text: SHORT_RATE, type: "bar", typeOptions: { fill: SHORT_COLOR } },
  ];

  const barChartDefaultOptions = {
    [TARGET_RATE] : { fill: TARGET_COLOR, xAxisId: 0 },
    [ON_TIME_RATE] : { fill: ON_TIME_COLOR, xAxisId: 1 },
    [DELAY_RATE] : { fill: DELAY_COLOR, xAxisId: 1 },
    [SHORT_RATE] : { fill: SHORT_COLOR, xAxisId: 1 },
  };
  
  const [fillRateBarChartSeries, setFillRateBarChartSeries] = useState(barChartDefaultSeries);
  const [fillRateBarChartOrgSeries, setFillRateBarChartOrgSeries] = useState(barChartDefaultSeries);

  const percentFormatter = params => {
    const value = numberUtil.formatNumberData(params, { maximumFractionDigits: 2 });

    return `${value}%`;
  }

  const ratioBarColumn = (colName, color) => {
    return {
      comparator: (valueA, valueB) => {return valueA - valueB},
      valueGetter: ({ data }) => data[colName],
      cellRenderer: RatioBarCell,
      cellRendererParams: { color, formatter: percentFormatter }
    }
  };

  const columnDefs = [
    { headerName: "categoryName", field: "categoryName", type: ["nonEditableColumn"], width: 240, filterMenuTab: true },
    { headerName: "skus", field: "skus", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 80 },
    { headerName: "demandQty", field: "demandQty", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 160  },
    { headerName: "shipmentQty", field: "salesQty", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 160  },
    { headerName: "backorderQty", field: "backorderQty", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 160  },
    { headerName: TARGET_RATE, field: TARGET_RATE, type: ["percentColumn", "nonEditableColumn", "rightAligned"], width: 230, ...ratioBarColumn(TARGET_RATE, TARGET_COLOR)},
    { headerName: ON_TIME_RATE, field: ON_TIME_RATE, type: ["percentColumn", "nonEditableColumn", "rightAligned"], width: 230,  ...ratioBarColumn(ON_TIME_RATE, ON_TIME_COLOR)},
    { headerName: "expectedFillRate", field: "expectedRate", type: ["percentColumn", "nonEditableColumn", "rightAligned"], width: 230,  ...ratioBarColumn("expectedRate", TOTAL_COLOR)},
  ];

  const getRequestParams = () => {
    const params = {};

    if (filterValue?.locations) {
      params.locCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (filterValue?.category) {
      params.categoryCd = filterValue.category.filter((category) => category.leafFlg).map((category) => category.hrchyCd);
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      params.item = itemSearch.current;
    }

    if (rpVersionFilter) {
      params.versionId = rpVersionFilter.id;
    }

    return { ...params };
  }

  const setChartData = (rpException) => {
    if (rpException) {
      const totalPieChartDatas = [
        {
          name: ON_TIME_RATE,
          value: rpException?.pieOnTimeCompleteRate,
          color: ON_TIME_COLOR
        },
        {
          name: SHORT_RATE,
          value: rpException?.pieShortExceptionRate,
          color: SHORT_COLOR
        },
        {
          name: DELAY_RATE,
          value: rpException?.pieDelayExceptionRate,
          color: DELAY_COLOR
        }
      ];
      totalPieChartDatas.sort((a, b) => a.value - b.value);

      setRpTotalExceptionPieChartData(totalPieChartDatas);

      setRpTotalExceptionBarChartData([rpException]);
    } else {
      setRpTotalExceptionPieChartData([]);

      setRpTotalExceptionBarChartData([]);
    }
  }

  const initResultData = () => {
    setFillRateCategoryData([]);
    setRpTotalExceptionBarChartData([]);
    setRpTotalExceptionPieChartData([]);
  }

  const retrieveRpFillRate = () => {
    initResultData();

    rpFillRateService.getAll(getRequestParams())
      .then(response => {
        const { fillRateCategory, rpException } = response.data;
        setFillRateCategoryData(fillRateCategory);

        const pageState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, filter: filterValue, rpVersion: rpVersionFilter });
        reduxDispatch(reduxUtil.updateViewState(pageState));

        const brushLength = fillRateCategory?.length ?? 0;
        setBrushStartEndIndex({...brushStartEndIndex, endIndex: Math.floor(brushLength / 2)})
        setChartData(rpException);
      })
      .catch(e => {
        debug(e);
      })
  }

  const labelRender = (entry) => {
    const RADIAN = Math.PI / 180;
    const { outerRadius, midAngle, fill, percent, name, cx, cy, index } = entry;
    const radius = outerRadius + 10;
    const positionX = cx + radius * Math.cos(-midAngle * RADIAN);
    const positionY = cy + radius * Math.sin(-midAngle * RADIAN);
    const formatData = { value: percent * 100 };
    
    // 위쪽이면 아래로, 아래쪽이면 위로 보정
    const verticalOffset = midAngle > 180 ? -5 : +5;

    // 겹침 방지용 Y 보정 (위쪽일 경우 index 기준으로 아래로 밀기)
    const isTopHalf = midAngle < 180;
    const yOffset = isTopHalf ? index * 12 : index * -12;
    const xOffset = isTopHalf ? index * -8 : index * 8;
    const textAnchor = positionX > cx ? "start" : "end";

    return (
      <g>
        <text
          x={positionX + xOffset}
          y={positionY + verticalOffset + yOffset} // 약간 위
          stroke="#000" // 윤곽선 색상 (검정)
          strokeWidth={0.8} // 테두리 두께
          paintOrder="stroke" // 테두리를 글자 뒤에 표시
          fill={fill}
          fontWeight={"bold"}
          textAnchor={textAnchor}
          fontSize={FS.H2}
        >
          {t(name)}
        </text>

        <text
          x={positionX + xOffset}
          y={positionY + verticalOffset + yOffset + 16}
          stroke="#000" // 윤곽선 색상 (검정)
          strokeWidth={0.8} // 테두리 두께
          paintOrder="stroke" // 테두리를 글자 뒤에 표시
          fill={fill}
          textAnchor={textAnchor}
          dominantBaseline="central"
          fontSize={FS.H1}
        >
          {percentFormatter(formatData)}
        </text>
      </g>
    );
  }

  const baseFormatter = (number) => {
    return numberUtil.formatNumber(number);
  }

  const tooltipFormatter = (value, name, toolTipProps) => {
    const formatValue = baseFormatter(value);

    return [formatValue, t(name), toolTipProps];
  }

  const percentageTooltipFormatter = (value, name, toolTipProps) => {
    const formatValue = `${Math.round(value * 100) / 100}%`;

    return [formatValue, t(name), toolTipProps];
  }

  /*
   * 조각 하나가 아니라 **모든 조각을 항상** 보여준다(의도된 내용) — 그래서 공용
   * AgStyleTooltipContent 로 대체하지 못하고, 생김새 상수만 빌려 같은 모양으로 맞춘다.
   */
  const PieCustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const keyName = payload[0].name;

      return (
        <AgTooltipBox key={`${keyName}_pie_tooltip`}>
          {rpTotalExceptionPieChartData.length > 0 && rpTotalExceptionPieChartData.map(({ name, value, color }) => (
            <AgTooltipRow key={`tooltip_${name}`} color={color} label={t(name)} value={`${Math.round(value * 100) / 100}%`} />
          ))}
        </AgTooltipBox>
      );
    }
    return null;
  }

  // 재고 예외 화면이 권한 밖이면 차트 클릭 드릴을 아예 붙이지 않는다(아래 onClick).
  const canOpenExceptions = isPermitted("INV_EXC");

  const showExceptionPage = (_) => {
    const reduxKey = "INV_EXC";
    const filter = { rpVersion: rpVersionFilter, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, filter: filterValue };
    const viewState = reduxUtil.createObjectState(reduxKey, filter);

    reduxDispatch(reduxUtil.updateViewState(viewState));
    reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
  }

  const dataToChartData = (p13nData)  => {
    if (p13nData?.chart && p13nData?.chart?.targetRate) {
      const series = []; 
      const orgSeries = [];
      
      const targetChart = p13nData.chart.targetRate;

      fillRateBarChartOrgSeries.forEach((target) => {
        const p13nSeriesData = targetChart[target.field]; 
        if (p13nSeriesData) {
          const result = {...target, type: p13nSeriesData.type.toLowerCase()}
          orgSeries.push(result);
          if (p13nSeriesData.activeFlg) {
            series.push(result);
          }
        }
      });

      let checkSortType = false;

      if (series.length > 1) {
        if (series[0].type === series[1].type) {
          checkSortType = true;
        }
      }
      
      if (checkSortType) {
        series.sort((a) => {
          if (a.id === "targetRate") {
            return -2;
          } else if (a.id === "expectedRate") {
            return -1;
          } else {
            return 0;
          }
        });
      }

      setFillRateBarChartSeries(checkSortType ? series : sortBy(series, ["type"]));
      setFillRateBarChartOrgSeries(checkSortType ? orgSeries : sortBy(orgSeries, ["type"]));
    }
  }

  const onGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  useEffect(() => {
    if (!isEmpty(rpVersionFilter) && isLoading) {
      retrieveRpFillRate();
      p13nService.get(viewName)
      .then(response => {
        const resultData = response.data;
        dataToChartData(resultData);
      })
      .catch(e => {
        debug(e);
      });    
    } else {
      setIsLoading(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpVersionFilter, filterValue, isLoading]);

  const handleChartConfigOpen = () => {
    setChartConfigOpen(!chartConfigOpen);
  };

  const brushRangeHandler = (event) => {
    setBrushStartEndIndex({...brushStartEndIndex, startIndex: event.startIndex, endIndex: event.endIndex});
  }

  const brushPositionHandler = (containerHeight) => {
    const brushHeight = containerHeight - 40;
    setBrushYPosition(brushHeight);
  }

  useEffect(()=> {
    if (chartId.length !== 0) {
      setChartConfigOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[chartId]);

  useEffect(()=> {
    if (chartId.length !== 0 && !chartConfigOpen) {
      setChartId("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[chartConfigOpen]);
  
  useEffect(() => {
    if (chartModalResultData.reload) {
      dataToChartData(chartModalResultData.content);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartModalResultData]);

  return (
    <>
      <FilterContainer hasIcons={false}>
        <RpVersionFilter viewName={viewName} rpVersionValue={rpVersionFilter} onChangeFilter={setRpVersionFilter} statusFilters={[SIMULATION, PLANNED, RELEASED]} />
        <LocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableMultiSelect selectLeafs/>
        <RPItemAutocomplete retrieve={retrieveRpFillRate} sx={{ mr: 1 }} searchValue={itemSearch} key={"ITEM"} extraCodesRef={extraItemCodesRef} />
      </FilterContainer>
      {/* .grid-area(전역 overflow:auto) 를 빼고 sx 로만 제어 — 서브픽셀 2px 초과로 스크롤바가 뜨던 것을 hidden 으로 차단.
          높이는 flex:1(부모 #tab-body 가 flex 세로)이 채운다. */}
      <Box sx={{ display: "flex", width: "100%", flex: 1, minHeight: 0, overflow: "hidden" }}>

        {/* left1 area */}
        <Box className={"chart-hover"} sx={{ minWidth: 400, width: "calc(24% - 12px)", height: "100%", mr: 1.5 }}>

          {/* Top */}
          <AppCard sx={{ width: "100%", height: "calc(50% - 6px)", mb: 1.5 }}>
            <Box sx={{ display: "flex", height: "45px", alignItems: "flex-end", justifyContent: "center" }}>
              <LeaderboardIcon fontSize={"small"} sx={{ mb: 0.35, mr: 0.25, color: "text.secondary" }} />
              <Typography className="chart-title" variant="h6" sx={{ pl: 1, textAlign: "center", fontSize: FS.H1, fontWeight: 600 }}>
                {t("replenishmentExceptionQty")}
              </Typography>
            </Box>
            <Box width={"100%"} height={"calc(100% - 45px)"}>
              <ResponsiveContainer width={"100%"} height={"100%"}>
                <BarChart
                  layout="vertical"
                  width={350}
                  height={200}
                  data={rpTotalExceptionBarChartData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: -20,
                    bottom: 5,
                  }}
                  onClick={canOpenExceptions ? showExceptionPage : undefined}
                  ref={exceptionChartRef}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" fontSize={FS.SUB} tickFormatter={(value) => { return baseFormatter(value) }} />
                  <YAxis dataKey="name" type="category" fontSize={FS.SUB} />
                  <Tooltip isAnimationActive={false} formatter={(value, name, etc) => { return tooltipFormatter(value, name, etc) }} content={<AgStyleTooltipContent />} />
                  <Legend
                    align="center"
                    margin={{
                      top: 5,
                      right: 0,
                      left: 30,
                      bottom: 5,
                    }}
                    formatter={(value) => { return t(value) }}
                  />
                  <Bar barSize={36} name={DEMAND_QTY} dataKey={DEMAND_QTY} fill={TARGET_COLOR}/>
                  <Bar barSize={36} name={ON_TIME_SHIPMENT} stackId="shipment" dataKey={ON_TIME_SHIPMENT} fill={ON_TIME_COLOR} />
                  <Bar barSize={36} name={DELAYED_QTY} stackId="shipment" dataKey={BACKORDER_QTY} fill={DELAY_COLOR} />
                  <Bar barSize={36} name={SHORT_QTY} stackId="shipment" dataKey={SHORT_QTY} fill={SHORT_COLOR} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </AppCard>

          {/* Bottom */}
          <AppCard sx={{ width: "100%", height: "calc(50% - 8px)"}}>
            <Box sx={{ display: "flex", height: "60px", alignItems: "flex-end", justifyContent: "center" }}>
              <PieChartIcon fontSize={"small"} sx={{ mb: 0.35, mr: 0.25, color: "text.secondary" }} />
              <Typography className="chart-title" variant="h6" sx={{ pl: 1, textAlign: "center", fontSize: FS.H1, fontWeight: 600 }}>
                {t("replenishmentExceptionFillRate")}
              </Typography>
            </Box>
            <Box width={"100%"} height={"calc(100% - 60px)"}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart width={400} height={300} onClick={canOpenExceptions ? showExceptionPage : undefined}>
                  <Pie
                    dataKey="value"
                    isAnimationActive={false}
                    data={rpTotalExceptionPieChartData}
                    cx="50%"
                    cy="50%"
                    startAngle={90}
                    endAngle={450}
                    outerRadius={"auto"}
                    fill="#8884d8"
                    label={labelRender}
                    labelLine={false}
                  >
                    {rpTotalExceptionPieChartData.length > 0 && rpTotalExceptionPieChartData.map((cellData) => {
                      return <Cell key={`cell-${cellData.name}`} fill={cellData.color} />
                    })}
                  </Pie>
                  <Tooltip content={<PieCustomTooltip />} />
                  <Legend margin={{ bottom: 15 }} align="center" formatter={(value) => { return t(value) }} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </AppCard>
        </Box>

        {/* right area */}
        <Box sx={{ minWidth: "800px", width: "76%", height: "100%" }}>

          {/* right main area */}
          {/* right top */}
          <Box sx={{ width: "100%", height: "100%" }}> 
            <AppCard sx={{ width: "100%", height: "calc(50% - 6px)", mb: 1.5 }}>
              <Box sx={{ position: "relative", display: "flex", height: "45px", alignItems: "flex-end", justifyContent: "center" }}>
                <LeaderboardIcon fontSize={"small"} sx={{ mb: 0.35, mr: 0.25, color: "text.secondary" }} />
                <Typography className="chart-title" variant="h6" sx={{ pl: 1, textAlign: "center", fontSize: FS.H1, fontWeight: 600 }}>
                  {t("fillRateItemCategory")}
                </Typography>
                <Box sx={{ position: "absolute", right: "0%", mt: 1.5, mr: 1 }}>
                  <SettingsButton name="chartConfig" label="chartConfig" onClick={() => setChartId("targetRate")} />
                </Box>
              </Box>
              <Box width={"100%"} height={"calc(100% - 45px)"}>
                <ResponsiveContainer className={"fill-rate-by-item-category"} width={"100%"} height={"100%"}
                  onResize={(_, containerHeight) => brushPositionHandler(containerHeight)}>
                  <ComposedChart
                    width={250}
                    height={400}
                    data={fillRateCategoryData}
                    margin={{
                      top: 5,
                      right: 95,
                      left: 50,
                      bottom: 50,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis xAxisId={0} dataKey="categoryName" angle={-23} fontSize={FS.SUB} dx={0} dy={20} />
                    <XAxis xAxisId={1} dataKey="categoryName" angle={-23} hide axisLine={false} />
                    <Brush
                      dataKey="categoryName"
                      height={25}
                      stroke={theme.palette.primary.main}
                      onChange={brushRangeHandler}
                      y={brushYPosition}
                    />
                    <YAxis unit="%" fontSize={FS.SUB} />
                    <Tooltip isAnimationActive={false} formatter={percentageTooltipFormatter} content={<AgStyleTooltipContent />} />
                    <Legend align="center" style={{ marginLeft: 50 }} formatter={(value) => { return t(value) }} />
                    {
                      fillRateBarChartSeries.map((series) => {
                        const chartOptions = barChartDefaultOptions[series.id];
                        
                        const stackId = series.id === "targetRate" ? "target" : "shipment";
                        if (series.type === "bar") {
                          return (<Bar barSize={35} xAxisId={chartOptions?.xAxisId} stackId={stackId} dataKey={series.id} fill={chartOptions?.fill} key={series.id} />)
                        } else if (series.type === "line") {
                          return (
                            <Line
                              dataKey={series.id}
                              stroke={chartOptions?.fill}
                              type="linear"
                              dor={{ r: 3, fill: TARGET_COLOR }}
                              strokeWidth={1.5}
                              key={series.id}
                            >
                            </Line>
                          )
                        } else if (series.type === "area") {
                          return (
                            <Area
                              dataKey={series.id}
                              stroke={chartOptions.fill}
                              fill={chartOptions.fill}
                              key={series.id}
                            >
                            </Area>
                          )
                        }
                        return ""
                      })
                    }
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </AppCard>

            {/* right bottom */}
            <AppCard sx={{ width: "100%", height: "calc(50% - 8px)" }}>
              <Box className="ag-theme-balham" width={"100%"} height={"100%"}>
                <AgGridReact
                  rowData={fillRateCategoryData}
                  {...defaultGridMemo}
                  onGridReady={onGridReady}
                />
              </Box>
            </AppCard>
          </Box>
        </Box>
      </Box>
      <ChartCommonConfigModal
        open={chartConfigOpen}
        setOpen={handleChartConfigOpen}
        viewName={viewName}
        dataSeries={fillRateBarChartOrgSeries}
        modalResultData={setChartModalResultData}
        chartId={chartId}
      />
    </>
  );
};

export default withTranslation()(FillRateDashboard);